import { useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import AppButton from '../../../../components/ui/AppButton';
import { useUpdateStateShipping } from '../../../../hooks/useShipping';
import { generateCode } from '../../../../libs/utils';
import { useNotificationStore } from '../../../../stores/notificationStore';

const ApproveDialog = ({ open, onClose, data }) => {
  const queryClient = useQueryClient();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [verifyCode, setVerifyCode] = useState('');
  const [verifyCodeError, setVerifyCodeError] = useState(false);
  const [input, setInput] = useState('');

  const { mutateAsync: updateStateShipping } = useUpdateStateShipping();

  const validateCode = () => {
    if (verifyCode === input) setVerifyCodeError(false);
    else setVerifyCodeError(true);
    return verifyCode === input;
  };

  const onApproveRedeem = async () => {
    const isValid = validateCode();

    if (isValid) {
      try {
        const response = await updateStateShipping({
          params: { id: data.shipping_id },
          dto: { state: 'APPROVE' },
        });

        if (response.Code === '20100') {
          setNotification('อนุมัติรายการแลกของรางวัลสำเร็จ', 'success');
          queryClient.invalidateQueries({ queryKey: ['shippingList'] });
          onClose();
        }
      } catch (err) {
        console.error(err);
        setNotification('อนุมัติรายการแลกของรางวัลไม่สำเร็จ', 'error');
      }
    }
  };

  useEffect(() => {
    if (open) {
      const code = generateCode(5);
      setVerifyCode(code);
    } else {
      setVerifyCode('');
    }
  }, [open]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiPaper-root': {
            maxWidth: 'unset',
          },
        }}
      >
        <Paper
          sx={{
            minWidth: '850px',
            backgroundColor: 'white',
            padding: '2rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 'bold',
              color: '#333',
              textAlign: 'center',
              marginBottom: '1.25rem',
              whiteSpace: 'pre-wrap',
            }}
          >
            ต้องการอนุมัติรายการแลกของรางวัล ใช่หรือไม่
          </Typography>

          <Paper
            elevation={0}
            sx={{
              borderTop: '1px solid rgba(224, 224, 224, 1)',
              borderLeft: '1px solid rgba(224, 224, 224, 1)',
              borderRight: '1px solid rgba(224, 224, 224, 1)',
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      background:
                        'linear-gradient(90deg, #002B38, #002B38, #002B38, #005D76, #006B7D)',
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '120px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      วันเวลา
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '100px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      รหัสบริษัท
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '150px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      ชื่อบริษัท
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '180px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      รางวัลที่แลก
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '100px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      จำนวน
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '1.25rem',
                        minWidth: '100px',
                        textAlign: 'center',
                        color: 'white',
                      }}
                    >
                      คะแนนที่ใช้
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {dayjs(data.create_timestamp).format('DD/MM/YYYY HH:mm:ss')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {data.company_code || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {data.company_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {data.reward_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {data.reward_quantity || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '1.125rem',
                          color: '#333',
                          textAlign: 'center',
                          marginBottom: '1.25rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {data.point_use}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Box
            sx={{
              width: '450px',
              margin: '0 auto',
              backgroundColor: '#f7f7f7',
              padding: '20px',
              borderRadius: '6px',
              marginTop: '20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
            }}
          >
            <Typography
              sx={{
                fontSize: '1.75rem',
                fontWeight: 'bold',
                color: '#333',
                textAlign: 'center',
                marginBottom: '10px',
                textTransform: 'uppercase',
              }}
            >
              Verification Required
            </Typography>
            <Box
              sx={{
                backgroundColor: '#eff6ff',
                border: '1px dashed #004B61',
                padding: '10px',
                borderRadius: '6px',
                width: '100%',
                height: '70px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: '2rem',
                letterSpacing: '.5rem',
              }}
            >
              {verifyCode}
            </Box>
            <Typography sx={{ fontSize: '1rem', color: '#a3a3a3', marginBottom: '20px' }}>
              Please enter the verification code to confirm the transaction
            </Typography>
            <TextField
              fullWidth
              value={input}
              onChange={(e) => setInput(e.target.value)}
              name="verify_code"
              placeholder="กรุณากรอกรหัสเพื่อยืนยัน"
              error={verifyCodeError ? true : false}
              inputProps={{ maxLength: 5 }}
              sx={{
                fontSize: '1.25rem',
                borderRadius: '8px',

                '& .MuiInputBase-root': {
                  width: '100%',
                  borderRadius: '8px',
                  backgroundColor: 'white',
                  textAlign: 'center',
                },
                '& .MuiInputBase-input': {
                  fontSize: '1.25rem',
                  padding: '0 10px',
                  height: '40px',
                  color: '#004B61',
                  textAlign: 'center',
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '1.25rem',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '8px',
                  border: '1px solid #004B61',
                },
              }}
            />

            {verifyCodeError && (
              <Typography
                sx={{
                  fontSize: '1rem',
                  textAlign: 'center',
                  color: '#ED1C24',
                  whiteSpace: 'pre-wrap',
                }}
              >
                รหัสไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '20px',
                maxWidth: '300px',
                margin: '40px auto 20px',
              }}
            >
              <AppButton
                variant="text"
                sx={{
                  width: '100px',
                  height: '40px',
                  fontSize: '1.25rem',
                  color: 'white',
                  backgroundColor: '#ED1C24',

                  '&:hover': {
                    color: 'white',
                    backgroundColor: '#ED1C24',
                    opacity: '.85',
                  },
                }}
                onClick={onClose}
              >
                ยกเลิก
              </AppButton>
              <AppButton
                variant="text"
                sx={{
                  width: '100px',
                  height: '40px',
                  fontSize: '1.25rem',
                  color: 'white',
                  backgroundColor: '#004B61',

                  '&:hover': {
                    color: 'white',
                    backgroundColor: '#004B61',
                    opacity: '.85',
                  },
                }}
                onClick={onApproveRedeem}
              >
                ตกลง
              </AppButton>
            </Box>
          </Box>
        </Paper>
      </Dialog>
    </>
  );
};

export default ApproveDialog;
